<template>
    <v-dialog
        :model-value="modelValue"
        max-width="900"
        width="95vw"
        :fullscreen="$vuetify.display.smAndDown"
        persistent
        scrollable
        :z-index="3200"
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <v-card>
            <v-toolbar density="comfortable">
                <v-toolbar-title>
                    Aplică și la alte produse ale furnizorului
                </v-toolbar-title>
                <v-spacer />
                <v-btn
                    icon
                    variant="text"
                    :disabled="saving"
                    @click="onCancel"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pt-4">
                <div class="mb-4">
                    <div class="text-caption text-medium-emphasis">Furnizor</div>
                    <div class="text-h6">{{ furnizorLabel || '—' }}</div>
                    <div class="text-body-2 text-medium-emphasis mt-1">
                        Produs sursă:
                        <strong>{{ sourceProductCode || '—' }}</strong>
                        <span v-if="sourceProductName">
                            — {{ sourceProductName }}
                        </span>
                    </div>
                </div>

                <div class="text-subtitle-2 mb-2">Ce copiez</div>
                <div class="d-flex flex-wrap ga-2 mb-2">
                    <v-btn
                        size="small"
                        variant="text"
                        @click="selectAllFields"
                    >
                        Toate câmpurile
                    </v-btn>
                    <v-btn
                        size="small"
                        variant="text"
                        @click="clearFields"
                    >
                        Niciun câmp
                    </v-btn>
                </div>
                <v-row dense class="mb-4">
                    <v-col
                        v-for="field in fieldDefinitions"
                        :key="field.key"
                        cols="12"
                        sm="6"
                        md="4"
                    >
                        <v-tooltip location="top" open-on-hover :open-delay="200">
                            <template #activator="{ props: tooltipProps }">
                                <div v-bind="tooltipProps">
                                    <v-checkbox
                                        v-model="selectedFieldKeys"
                                        :label="field.label"
                                        :value="field.key"
                                        density="compact"
                                        hide-details
                                        :disabled="saving"
                                    />
                                </div>
                            </template>
                            <span>
                                Valoare curentă:
                                <strong>{{ fieldCurrentValue(field.key) }}</strong>
                            </span>
                        </v-tooltip>
                    </v-col>
                </v-row>

                <div class="d-flex flex-wrap align-center justify-space-between ga-2 mb-2">
                    <div class="text-subtitle-2 mb-0">
                        Pe ce produse
                        <span class="text-medium-emphasis font-weight-regular">
                            ({{ selectedProductCodes.length }} / {{ filteredPeers.length }})
                        </span>
                    </div>
                    <div class="d-flex flex-wrap ga-2">
                        <v-btn
                            size="small"
                            variant="text"
                            :disabled="!filteredPeers.length || saving"
                            @click="selectAllProducts"
                        >
                            Selectează toate
                        </v-btn>
                        <v-btn
                            size="small"
                            variant="text"
                            :disabled="!selectedProductCodes.length || saving"
                            @click="clearProducts"
                        >
                            Niciuna
                        </v-btn>
                    </div>
                </div>

                <v-text-field
                    v-model="search"
                    label="Căutare (cod / denumire)"
                    density="compact"
                    variant="outlined"
                    clearable
                    hide-details="auto"
                    prepend-inner-icon="mdi-magnify"
                    class="mb-3"
                    :disabled="saving"
                />

                <v-alert
                    v-if="!peers.length"
                    type="info"
                    variant="tonal"
                    density="compact"
                    class="mb-0"
                >
                    Nu există alte produse cu acest furnizor în lista curentă.
                </v-alert>

                <v-table
                    v-else
                    density="compact"
                    class="propagate-peers-table"
                >
                    <thead>
                        <tr>
                            <th style="width: 48px" class="propagate-select-col">
                                <v-checkbox
                                    :model-value="allFilteredProductsSelected"
                                    :indeterminate="someFilteredProductsSelected"
                                    density="compact"
                                    hide-details
                                    :disabled="!filteredPeers.length || saving"
                                    aria-label="Selectează toate produsele"
                                    @update:model-value="toggleAllFilteredProducts"
                                />
                            </th>
                            <th>Cod</th>
                            <th>Denumire</th>
                            <th class="text-end">Transport</th>
                            <th class="text-end">Preț</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="peer in filteredPeers"
                            :key="peer.product_code"
                        >
                            <td>
                                <v-checkbox
                                    v-model="selectedProductCodes"
                                    :value="peer.product_code"
                                    density="compact"
                                    hide-details
                                    :disabled="saving"
                                />
                            </td>
                            <td>
                                <span class="font-weight-medium">{{ peer.product_code }}</span>
                                <v-chip
                                    v-if="peer.hasDraft"
                                    class="ms-1"
                                    size="x-small"
                                    color="warning"
                                    variant="tonal"
                                >
                                    în lucru
                                </v-chip>
                            </td>
                            <td>{{ peer.product_name }}</td>
                            <td class="text-end">
                                {{ formatTransport(peer.transportCostRate) }}
                            </td>
                            <td class="text-end">
                                {{ formatPrice(peer.price) }}
                            </td>
                        </tr>
                        <tr v-if="!filteredPeers.length">
                            <td colspan="5" class="text-medium-emphasis text-center py-4">
                                Niciun produs nu corespunde căutării.
                            </td>
                        </tr>
                    </tbody>
                </v-table>
            </v-card-text>

            <v-card-actions class="pa-4 flex-wrap ga-2">
                <v-spacer class="d-none d-sm-flex" />
                <v-btn
                    variant="text"
                    :disabled="saving"
                    @click="onCancel"
                >
                    Anulează
                </v-btn>
                <v-btn
                    variant="text"
                    :disabled="saving"
                    @click="onSkip"
                >
                    Doar acest produs
                </v-btn>
                <v-btn
                    color="primary"
                    variant="elevated"
                    style="color: white !important"
                    :loading="saving"
                    :disabled="!canApply"
                    @click="onApply"
                >
                    Salvează și aplică
                    <template v-if="selectedProductCodes.length">
                        ({{ selectedProductCodes.length }})
                    </template>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import {
    PROPAGATE_FIELD_DEFINITIONS,
    filterPropagatePeers,
    formatPropagateFieldCurrentValue,
    formatPropagateTransportDisplay,
} from '@/utils/priceListPropagateFurnizor';
import { formatRonPrice } from '@/utils/utils';

export default {
    name: 'PriceListPropagateFurnizorFieldsDialog',
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        furnizorLabel: {
            type: String,
            default: '',
        },
        sourceProductCode: {
            type: String,
            default: '',
        },
        sourceProductName: {
            type: String,
            default: '',
        },
        peers: {
            type: Array,
            default: () => [],
        },
        /** Field keys pre-checked when dialog opens. */
        defaultFieldKeys: {
            type: Array,
            default: () => [],
        },
        /** Source product builder form — used for checkbox tooltips. */
        sourceForm: {
            type: Object,
            default: null,
        },
        saving: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'skip', 'apply', 'cancel'],
    data() {
        return {
            fieldDefinitions: PROPAGATE_FIELD_DEFINITIONS,
            selectedFieldKeys: [],
            selectedProductCodes: [],
            search: '',
        };
    },
    computed: {
        filteredPeers() {
            return filterPropagatePeers(this.peers, this.search);
        },
        filteredProductCodes() {
            return this.filteredPeers
                .map((peer) => peer.product_code)
                .filter(Boolean);
        },
        selectedFilteredCount() {
            const selected = new Set(this.selectedProductCodes);
            return this.filteredProductCodes.filter((code) => selected.has(code)).length;
        },
        allFilteredProductsSelected() {
            const codes = this.filteredProductCodes;
            return codes.length > 0
                && this.selectedFilteredCount === codes.length;
        },
        someFilteredProductsSelected() {
            return this.selectedFilteredCount > 0
                && !this.allFilteredProductsSelected;
        },
        canApply() {
            return (
                this.selectedFieldKeys.length > 0
                && this.selectedProductCodes.length > 0
                && !this.saving
            );
        },
    },
    watch: {
        modelValue(isOpen) {
            if (isOpen) {
                this.resetSelection();
            }
        },
        peers() {
            if (this.modelValue) {
                this.syncProductSelectionWithPeers();
            }
        },
    },
    methods: {
        fieldCurrentValue(fieldKey) {
            return formatPropagateFieldCurrentValue(fieldKey, this.sourceForm);
        },
        resetSelection() {
            const defaults = Array.isArray(this.defaultFieldKeys)
                ? this.defaultFieldKeys.filter((key) => (
                    PROPAGATE_FIELD_DEFINITIONS.some((field) => field.key === key)
                ))
                : [];
            this.selectedFieldKeys = [...defaults];
            this.search = '';
            // Same as bulk update: no auto-selection; user picks rows or uses header/buttons.
            this.selectedProductCodes = [];
        },
        syncProductSelectionWithPeers() {
            const allowed = new Set(
                (this.peers || []).map((peer) => peer.product_code),
            );
            this.selectedProductCodes = this.selectedProductCodes.filter(
                (code) => allowed.has(code),
            );
        },
        selectAllFields() {
            this.selectedFieldKeys = PROPAGATE_FIELD_DEFINITIONS.map(
                (field) => field.key,
            );
        },
        clearFields() {
            this.selectedFieldKeys = [];
        },
        selectAllProducts() {
            this.selectedProductCodes = this.filteredPeers.map(
                (peer) => peer.product_code,
            );
        },
        clearProducts() {
            this.selectedProductCodes = [];
        },
        toggleAllFilteredProducts(checked) {
            if (checked) {
                const selected = new Set(this.selectedProductCodes);
                for (const code of this.filteredProductCodes) {
                    selected.add(code);
                }
                this.selectedProductCodes = [...selected];
                return;
            }
            const filtered = new Set(this.filteredProductCodes);
            this.selectedProductCodes = this.selectedProductCodes.filter(
                (code) => !filtered.has(code),
            );
        },
        formatTransport(value) {
            return formatPropagateTransportDisplay(value);
        },
        formatPrice(value) {
            if (value === null || value === undefined || value === '') {
                return '—';
            }
            return formatRonPrice(value);
        },
        onCancel() {
            this.$emit('cancel');
            this.$emit('update:modelValue', false);
        },
        onSkip() {
            this.$emit('skip');
            this.$emit('update:modelValue', false);
        },
        onApply() {
            if (!this.canApply) {
                return;
            }
            this.$emit('apply', {
                fieldKeys: [...this.selectedFieldKeys],
                targetProductCodes: [...this.selectedProductCodes],
            });
        },
    },
};
</script>

<style scoped>
.propagate-peers-table {
    max-height: 360px;
    overflow: auto;
}

.propagate-select-col {
    vertical-align: middle;
}
</style>
